import type { FC } from 'react';
import { memo, useMemo } from 'react';
import { Grid } from '@mui/material';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type {
  DashboardWidgetConfig,
  GridSizeOrGrow,
} from 'src/types/dashboard';
import { dashboardHiddenSections } from 'src/components/dashboard/DashboardPage/atom';
import useGridSize from 'src/components/dashboard/hooks/useGridSize';

import WidgetContextProvider from './components/widgetContextProvider';
import { getWidget, getWidgetGridWrapper, getWidgetLegend } from './widgetMaps';

// helper for converting GridSize to what the Grid component now accepts
const toGridProp = (size: GridSizeOrGrow): boolean | 'auto' | number => {
  if (size === 'grow') return true;
  return size;
};

export const WIDGET_WRAPPER_TEST_ID = 'dashboard-widget';

export interface WidgetWrapperProps {
  config: DashboardWidgetConfig;
  isFirstOfWidgetType: boolean;
  sectionId: number | undefined;
}

export const WidgetWrapper: FC<WidgetWrapperProps> = ({
  config,
  isFirstOfWidgetType,
  sectionId,
}) => {
  const { widget_type: widgetType } = config;
  const hiddenSections = useAtomValue(dashboardHiddenSections);
  const hidden = useMemo(
    () =>
      hiddenSections.find((hiddenSectionId) => hiddenSectionId === sectionId) &&
      !(config.widget_type === DashboardWidgetType.SECTION),
    [hiddenSections],
  );
  const className = classNames(
    {
      widgetHidden: hidden,
    },
    !!sectionId && `widget-section-${sectionId}`,
  );

  const gridSize = useGridSize(config);
  const [Widget, GridWrapper, WidgetLegend] = useMemo(
    () => [
      getWidget(widgetType),
      getWidgetGridWrapper(widgetType),
      isFirstOfWidgetType && getWidgetLegend(widgetType),
    ],
    [widgetType, isFirstOfWidgetType],
  );

  if (!Widget) return null;

  return (
    <>
      {!!WidgetLegend && (
        <Grid item xs={12} lg={toGridProp(gridSize)}>
          <WidgetLegend />
        </Grid>
      )}
      <GridWrapper config={config} gridSize={gridSize} className={className}>
        <WidgetContextProvider config={config} widgetType={widgetType}>
          <Widget config={config} className={className} />
        </WidgetContextProvider>
      </GridWrapper>
    </>
  );
};

export default memo(WidgetWrapper);
